/* eslint-disable max-lines-per-function */
import { A } from '@adornis/base/env-info.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { generateParticipationSigningListPdf } from '@adornis/buchungsheld-db/api/workshop.js';
import { Certificate } from '@adornis/buchungsheld-db/entities/certificate.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { getPluginForScope } from '@adornis/buchungsheld-db/plugins/plugin.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-section.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import '@adornis/chemistry/elements/components/x-tabs.js';
import '@adornis/forms/x-input.js';
import { openPopupWithLoadingScreen } from '@adornis/print/client/safe-popup.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { filter, switchMap } from 'rxjs';
import '../../../../components/bh-participation-dataview.js';
import { BHPromptProductParticipationQR } from '../../../../prompts/bh-prompt-product-participation-qr.js';
import { BHPromptUrlQR } from '../../../../prompts/bh-prompt-url-qr.js';
import { BHPromptWorkshopQR } from '../../../../prompts/bh-prompt-workshop-qr.js';
import { BHPromptParticipationModify } from '../../../../prompts/participation-modify/bh-prompt-participation-modify.js';

/**
 * @element bh-page-admin-einrichtung-workshops-edit
 */
@customElement('bh-page-admin-einrichtung-workshops-edit')
export class BHPageAdminEinrichtungWorkshopsEdit extends BuchungsheldLitElement {
  @property({ attribute: 'institution-id' }) institutionID?: string;
  @property({ attribute: 'workshop-id' }) workshopID = new RXController<string | null>(this, null);

  @state() private readonly _workshop = new RXController(
    this,
    this.workshopID.observable.pipe(
      filter(Boolean),
      switchMap(id =>
        subscribeByID(
          Workshop,
          id,
        )({
          name: 1,
          institutionID: 1,
          appointments: { name: 1, start: 1, end: 1, location: 1 },
          settings: {
            _id: 1,
            extraParticipationConstraints: 1,
            inheritedExtraParticipationConstraints: 1,
          },
        }),
      ),
    ),
  );

  override render() {
    if (this.workshopID.isLoading || this._workshop.isLoading) {
      return html` <x-loading-state></x-loading-state> `;
    }

    this.exposeAction('addParticipation', {
      icon: 'add',
      text: this._translation.translate('PARTICIPATION_DATAVIEW', 'addParticipant'),
      callback: async () => {
        const product = this._workshop.value!;
        await BHPromptParticipationModify.add(product._id, Workshop._class, product.institutionID);
      },
    });

    if (!this.institutionID) throw new Error('institutionID is not defined');
    const paymentPlugin = getPluginForScope('payment', this.institutionID);
    const onlyShowRegistrationLink = paymentPlugin.id === 'payment::immediate-participation';

    const globalActions = [
      ...Array.from(
        function* (this: BHPageAdminEinrichtungWorkshopsEdit) {
          yield {
            icon: 'qr_code_2',
            text: onlyShowRegistrationLink
              ? this._translation.translate('workshop', 'generate_participation_qrcode')
              : this._translation.translate('workshop', 'generate_registration_qrcode'),
            action: () =>
              BHPromptUrlQR.showPopup({
                props: {
                  url: A.absoluteUrl(`/product/${this.workshopID.value}`),
                },
              }),
          };

          if (!onlyShowRegistrationLink)
            yield {
              action: () =>
                this._workshop.value?.appointments.length ?? 0 > 0
                  ? BHPromptWorkshopQR.showPopup({ props: { workshopID: this.workshopID.value } })
                  : BHPromptProductParticipationQR.showPopup({ props: { productID: this.workshopID.value } }),
              icon: 'qr_code_2',
              text: this._translation.translate('workshop', 'generate_participation_qrcode'),
            };

          yield {
            icon: 'approval',
            text: this._translation.translate('workshop', 'send_certificates'),
            action: () => this.sendCertificates().catch(this.e),
          };

          yield {
            icon: 'check_box',
            text: this._translation.translate('workshop', 'generate_participation_signing_list'),
            action: () => this.downloadParticipationSigningListPDF(),
          };
        }.bind(this)(),
      ),
    ].map(
      ({ icon, text, action }) => html`
        <x-flex
          horizontal
          crossaxis-center
          space="sm"
          padding="xxs"
          ${css({ color: this.colors.accent, cursor: 'pointer' })}
          @click=${action}
        >
          <x-icon> ${icon} </x-icon>
          <x-text>${text}...</x-text>
        </x-flex>
      `,
    );

    return html`
      <x-flex padding="md" space="xs"> ${globalActions} </x-flex>

      <x-hr> </x-hr>

      ${this._workshopContent()}
    `;
  }

  private _workshopContent() {
    const workshop = this._workshop.value;
    if (!workshop) return nothing;

    return html`
      <x-flex space="xl">
        ${this._importExportSection(workshop)}
        <x-hr></x-hr>
        <bh-participation-dataview .product=${workshop}></bh-participation-dataview>
      </x-flex>
    `;
  }

  private _importExportSection(
    workshop: NarrowedEntity<Workshop, { _id: 1; name: 1; appointments: { start: 1; end: 1 } }>,
  ) {
    if (!this.institutionID || !this.workshopID.value) return nothing;
    const plugin = getPluginForScope('workshop-participation-export', this.institutionID);
    return plugin.render(this, this._translation, this.institutionID, workshop);
  }

  async sendCertificates() {
    const workshop = this._workshop.value;
    if (!workshop) throw new Error('workshop not loaded yet');

    // ask before sending mails
    const question = this._translation.translate('workshop', 'question_send_certificates');
    const reallySentCertificates = await TrDialog.confirm(question);
    if (!reallySentCertificates) return;

    // send mails
    await TrDialog.waitFor(Certificate.sendCertificateToProductParticipantsWhichHaveParticipated(workshop._id)());
    void XSnackbar.show(this._translation.translate('workshop', 'successfully_sent_certificates'));
  }

  async downloadParticipationSigningListPDF() {
    try {
      const workshopID = this.workshopID.value;
      if (!workshopID) throw new Error('workshopID is not defined');

      const getLink = generateParticipationSigningListPdf(workshopID)();
      await openPopupWithLoadingScreen(getLink);
    } catch (err) {
      console.error(err);
      XSnackbar.show(this._translation.translate('err', 'could_not_process_data'));
    }
  }
}
